import type { Dispatch, SetStateAction } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '../../catalyst/button';
import { Checkbox, CheckboxField } from '../../catalyst/checkbox';
import { Label } from '../../catalyst/fieldset';
import AuthGuard from '../AuthGuard';

export interface DocumentPagesHeaderFiltersProps {
  filteredCount: number;
  totalCount: number;
  showFilterDropdown: boolean;
  setShowFilterDropdown: Dispatch<SetStateAction<boolean>>;
  showAdvancedView: boolean;
  setShowAdvancedView: Dispatch<SetStateAction<boolean>>;
  showOnlyNoCategory: boolean;
  setShowOnlyNoCategory: Dispatch<SetStateAction<boolean>>;
  showOnlyErrors: boolean;
  setShowOnlyErrors: Dispatch<SetStateAction<boolean>>;
  showOnlyStranded: boolean;
  setShowOnlyStranded: Dispatch<SetStateAction<boolean>>;
  strandedCount: number;
  showOnlyLowClassificationScore: boolean;
  setShowOnlyLowClassificationScore: Dispatch<SetStateAction<boolean>>;
  showOnlyProcessing: boolean;
  setShowOnlyProcessing: Dispatch<SetStateAction<boolean>>;
  pagesWithNoCategoryCount: number;
  pagesWithErrorsCount: number;
  pagesWithLowClassificationScoreCount: number;
  pagesProcessingCount: number;
}

export function DocumentPagesHeaderFilters({
  filteredCount,
  totalCount,
  showFilterDropdown,
  setShowFilterDropdown,
  showAdvancedView,
  setShowAdvancedView,
  showOnlyNoCategory,
  setShowOnlyNoCategory,
  showOnlyErrors,
  setShowOnlyErrors,
  showOnlyStranded,
  setShowOnlyStranded,
  strandedCount,
  showOnlyLowClassificationScore,
  setShowOnlyLowClassificationScore,
  showOnlyProcessing,
  setShowOnlyProcessing,
  pagesWithNoCategoryCount,
  pagesWithErrorsCount,
  pagesWithLowClassificationScoreCount,
  pagesProcessingCount,
}: DocumentPagesHeaderFiltersProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-lg font-medium mb-4 text-gray-500 dark:text-zinc-400">
        Document Pages
        <p>
          <span className="text-xs text-gray-500 dark:text-zinc-400">
            Showing {filteredCount} of {totalCount} pages
          </span>
        </p>
      </h2>

      <div className="relative inline-block text-left">
        <Button color="white/dark" onClick={() => setShowFilterDropdown((prev) => !prev)}>
          Filters
          <ChevronDownIcon />
        </Button>

        {showFilterDropdown && (
          <div className="origin-top-right absolute right-0 mt-2 w-92 rounded-md shadow-lg bg-white dark:bg-zinc-800 ring-1 ring-gray-300 dark:ring-zinc-600 ring-opacity-5 z-20 p-2">
            <div className="flex flex-col gap-2">
              <AuthGuard roles={['admin', 'curator']}>
                <CheckboxField>
                  <Checkbox
                    color="indigo"
                    checked={showAdvancedView}
                    onChange={() => setShowAdvancedView((v) => !v)}
                  />
                  <Label>Advanced view</Label>
                </CheckboxField>
              </AuthGuard>
              <CheckboxField disabled={pagesWithNoCategoryCount === 0}>
                <Checkbox
                  color="indigo"
                  checked={showOnlyNoCategory}
                  onChange={() => setShowOnlyNoCategory((v) => !v)}
                />
                <Label>Show only pages with no category ({pagesWithNoCategoryCount})</Label>
              </CheckboxField>
              <CheckboxField disabled={pagesWithErrorsCount === 0}>
                <Checkbox color="indigo" checked={showOnlyErrors} onChange={() => setShowOnlyErrors((v) => !v)} />
                <Label>Show only pages with errors ({pagesWithErrorsCount})</Label>
              </CheckboxField>
              <CheckboxField disabled={strandedCount === 0}>
                <Checkbox color="amber" checked={showOnlyStranded} onChange={() => setShowOnlyStranded((v) => !v)} />
                <Label>Show only stranded pages ({strandedCount})</Label>
              </CheckboxField>
              <CheckboxField disabled={pagesWithLowClassificationScoreCount === 0}>
                <Checkbox
                  color="indigo"
                  checked={showOnlyLowClassificationScore}
                  onChange={() => setShowOnlyLowClassificationScore((v) => !v)}
                />
                <Label>
                  Show only pages with low classification score ({pagesWithLowClassificationScoreCount})
                </Label>
              </CheckboxField>
              <CheckboxField disabled={pagesProcessingCount === 0}>
                <Checkbox
                  color="indigo"
                  checked={showOnlyProcessing}
                  onChange={() => setShowOnlyProcessing((v) => !v)}
                />
                <Label>Show only processing pages ({pagesProcessingCount})</Label>
              </CheckboxField>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
